/**
 * Tiny WebAudio synth for the game concept. No audio files. OFF by default (a B2B form opened at someone's desk must not
 * make noise unasked); the speaker button in the HUD switches it on, and the choice is remembered per browser. Every call
 * is a no-op while it is off, and never throws.
 */
let ctx: AudioContext | null = null;
const KEY = 'gm_sound';

export const soundEnabled = (): boolean => {
  try { return typeof window !== 'undefined' && window.localStorage.getItem(KEY) === '1'; } catch { return false; }
};
export const setSoundEnabled = (on: boolean) => {
  try { window.localStorage.setItem(KEY, on ? '1' : '0'); } catch { /* ignore */ }
};

function audio(): AudioContext | null {
  if (!soundEnabled()) return null;
  try {
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    ctx = ctx || new AC();
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch { return null; }
}

function tone(freq: number, start: number, dur: number, type: OscillatorType = 'sine', vol = 0.05) {
  const c = audio(); if (!c) return;
  const t0 = c.currentTime + start;
  const o = c.createOscillator(); const g = c.createGain();
  o.type = type; o.frequency.setValueAtTime(freq, t0);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(vol, t0 + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  o.connect(g).connect(c.destination);
  o.start(t0); o.stop(t0 + dur + 0.02);
}

/** the power-up ladder: C5 D5 E5 G5 A5 C6 E6 (rising major scale / pentatonic), one rung per NMLS character (7 max) */
const LADDER = [523, 587, 659, 784, 880, 1047, 1319];

export const sfx = {
  /** any button / answer / link */
  tap() { tone(760, 0, 0.05, 'triangle', 0.045); tone(1140, 0.03, 0.06, 'triangle', 0.03); },
  /** a checkbox / multi-select item switched ON (rising) */
  on() { tone(600, 0, 0.07, 'triangle', 0.05); tone(900, 0.055, 0.1, 'triangle', 0.045); },
  /** switched OFF (falling) */
  off() { tone(700, 0, 0.07, 'triangle', 0.04); tone(430, 0.055, 0.1, 'triangle', 0.035); },
  /** a dropdown opens */
  open() { tone(420, 0, 0.09, 'sine', 0.04); tone(640, 0.05, 0.1, 'sine', 0.035); },
  /** primary call-to-action */
  cta() { tone(523, 0, 0.09, 'triangle', 0.05); tone(784, 0.06, 0.09, 'triangle', 0.05); tone(1047, 0.12, 0.16, 'triangle', 0.045); },
  /** back / quiet */
  back() { tone(520, 0, 0.06, 'sine', 0.035); tone(390, 0.04, 0.08, 'sine', 0.03); },
  /** an answer lands */
  pick() { tone(660, 0, 0.09, 'triangle', 0.05); tone(990, 0.06, 0.12, 'triangle', 0.045); },
  /** streak tick (pitch climbs with the streak) */
  streak(n: number) { tone(520 + Math.min(n, 8) * 55, 0, 0.1, 'square', 0.025); },
  /** level up */
  level() { [523, 659, 784, 1047].forEach((f, i) => tone(f, i * 0.09, 0.22, 'triangle', 0.055)); },
  /** badge */
  badge() { tone(880, 0, 0.12, 'sine', 0.05); tone(1320, 0.09, 0.2, 'sine', 0.045); },
  /** NMLS typing: a power-up ladder. The Nth character typed plays the Nth rung of a rising major scale (C5 up to E6), so the pitch
   * climbs one step with every character from the first to the last, whatever the digits are. `from` = how many characters were already
   * there; a paste climbs through its rungs fast. A bright chip square wave with a glassy octave on top, like a power-up. */
  digits(typed: string, from: number) {
    const n = Math.min(typed.length, LADDER.length - from);
    for (let i = 0; i < n; i++) {
      const f = LADDER[from + i]; const at = i * 0.05; const top = from + i === LADDER.length - 1;
      tone(f, at, top ? 0.16 : 0.07, 'square', 0.02); tone(f * 2, at + 0.012, top ? 0.2 : 0.08, 'sine', top ? 0.02 : 0.012);
    }
  },
  /** a character deleted: the rung you just stepped off, one lower than before, so the ladder walks back down */
  erase(length: number) { tone(length > 0 ? LADDER[Math.min(length, LADDER.length) - 1] : 392, 0, 0.05, 'triangle', 0.028); },
  /** plan unlocked */
  unlock() { [392, 494, 587, 784, 988, 1175].forEach((f, i) => tone(f, i * 0.08, 0.3, 'triangle', 0.05)); },
};
